export interface TxPermitSummary {
  permitNumber: string;
  status: string;
  permitType?: string;
  apiNumber?: string;
  wellName?: string;
  wellNumber?: string;
  leaseName?: string;
  operatorName?: string;
  operatorNumber?: string;
  county: string;
  district?: string;
  fieldName?: string;
  fieldNumber?: string;
  wellType?: string;
  totalDepthFt?: number;
  lateralLengthFt?: number;
  latitude?: number;
  longitude?: number;
  permitDate?: string;
  submittedDate?: string;
  importedAt?: string;
  lastUpdated?: string;
  sourceLabel?: string;
  sourceStatus?: 'official_import' | 'inventory_fallback';
  freshnessStatus?: 'fresh' | 'aging' | 'stale' | 'unknown';
  locationConfidence?: 'source_coordinates' | 'registry_location';
}

export interface TxViolationSummary {
  id: string;
  inspectionNumber: string;
  violationCode?: string;
  violationDescription?: string;
  severity?: string;
  status?: string;
  inspectionDate?: string;
  resolutionDate?: string;
  leaseNumber?: string;
  wellApiNumber?: string;
  operatorName?: string;
  operatorNumber?: string;
  county?: string;
  district?: string;
  fieldName?: string;
  penaltyAmount?: number;
  description?: string;
  matchSource?: 'api' | 'lease';
}

export interface TxActivitySummary {
  operatorName?: string;
  operatorNumber?: string;
  leaseName?: string;
  fieldName?: string;
  fieldNumber?: string;
  latestProductionMonth?: string;
  latestOilBbls?: number;
  latestGasMcf?: number;
  latestCondensateBbls?: number;
  trailing3OilBbls?: number;
  trailing3GasMcf?: number;
  trailing3CondensateBbls?: number;
  trailing6OilBbls?: number;
  trailing6GasMcf?: number;
  trailing6CondensateBbls?: number;
  trailing12OilBbls?: number;
  trailing12GasMcf?: number;
  trailing12CondensateBbls?: number;
  trendDirection?: 'up' | 'down' | 'flat';
  trendBasis?: 'oil' | 'gas';
}

export interface TxOfficialWaterSummary {
  status: 'available' | 'unavailable';
  waterBblPerDay?: number;
  sourceForm?: 'W10' | 'G10';
  testDate?: string;
  effectiveDate?: string;
  cycleMonth?: string;
  measurementKind?: 'official_status_test_daily_rate';
  warning?: string;
  sourceLabel?: string;
  matchedApiNumber?: string;
  leaseKey?: string;
  matchConfidence?: string;
}

export interface TxH2SSummary {
  certificateNumber: string;
  district?: string;
  counties?: string[];
  reportedPpm?: number;
  reportedMcfPerDay?: number;
  publicAreaRoeRequired?: boolean;
  publicRoadRoeRequired?: boolean;
  approvalDate?: string;
  termDate?: string;
  amendedDate?: string;
  drillingComplianceDepth?: number;
  fieldName?: string;
  fieldNumber?: string;
  facilityType?: string;
  facilityId?: string;
  facilityActive?: boolean;
  leaseOrFacilityName?: string;
  leaseOrFacilityNumber?: string;
  operatorName?: string;
  operatorNumber?: string;
  leaseName?: string;
  oilGasCode?: string;
  roe100Ft?: number;
  roe500Ft?: number;
  h2sInjection?: boolean;
  comments?: string;
  sourceLabel?: string;
  matchBasis?: 'lease_number' | 'facility_number' | 'facility_id';
}

export interface WellLifecycleSummary {
  permitSubmittedDate?: string;
  permitApprovedDate?: string;
  spudDate?: string;
  completionDate?: string;
  firstProductionMonth?: string;
  latestProductionMonth?: string;
  plugDate?: string;
  latestActivityDate?: string;
  latestActivityType?: 'permit' | 'production' | 'completion' | 'plug' | 'spud';
}

export interface NearbyOpsFacilitySummary {
  id: string;
  name: string;
  distanceMiles?: number;
  typeLabel?: string;
  operatorName?: string;
  county?: string;
  commodity?: string;
  permitNumber?: string;
  status?: string;
}

export interface NearbyOpsSummary {
  sameLeaseWellCount?: number;
  sameFieldWellCount?: number;
  nearestSwd?: NearbyOpsFacilitySummary;
  nearestGasPlant?: NearbyOpsFacilitySummary;
  nearestPipeline?: NearbyOpsFacilitySummary;
}

export interface FacilityDetailSummary {
  permitNumber?: string;
  serialNumber?: string;
  source?: string;
  capacity?: string;
  capacityMcf?: string;
  wellDepth?: string;
  contractor?: string;
  rigType?: string;
  currentWell?: string;
  systemName?: string;
  subsystemName?: string;
  commodity?: string;
  diameterInches?: number;
  systemType?: string;
  lengthMiles?: number;
  isInterstate?: boolean;
  isCommonCarrier?: boolean;
  symbology?: string;
  district?: string;
  wellType?: string;
  pool?: string;
}

export interface OperatorStatusSummary {
  label: 'Operator status';
  shortLabel: 'Operator';
  source: 'NM OCD operator search registry';
  ogrid: string;
  name: string;
  status: string;
  statusTooltip: 'Active in OCD export. Not live field activity.';
  companionLine: 'Official OCD operator registry/status summary. "Active" means the operator appears in the OCD export with inactive operators excluded; it does not indicate live field activity or current operations.';
  importedAt?: string;
  lastUpdated?: string;
  matchBasis: 'ogrid' | 'operator_name';
}

export interface C148VolumeCell {
  key: string;
  label: string;
  value: number;
  unit: 'bbl';
  display: string;
}

export interface C148ProducedWaterPeriod {
  reportingPeriod: string;
  reportingPeriodDate: string;
  reportingPeriodLabel?: string;
  recyclingFacilityCount?: number;
  volumes: C148VolumeCell[];
}

export interface C148ProducedWaterVolumesSummary {
  label: 'C-148 volumes';
  shortLabel: 'C-148';
  source: 'NM OCD C-148 produced-water volume reports';
  companionLine: 'Official OCD C-148 produced-water volume reports. Monthly summaries per operator. Not current water movement, recycling facility locations, or live water transfers.';
  unit: 'bbl';
  unitBasis: 'reported_barrels_from_ocd_operator_summary';
  ogrid: string;
  operator: string;
  matchBasis: 'ogrid' | 'operator_name';
  importedAt?: string;
  lastUpdated?: string;
  latestReportingPeriod?: string;
  unitConfirmed?: boolean;
  periods: C148ProducedWaterPeriod[];
}

export interface Well {
  id: string;
  api: string;
  identifier?: string;
  wellName: string;
  operator: string;
  latitude: number;
  longitude: number;
  status: string;
  basin: string;
  county: string;
  state?: string;
  type?: string;
  sourceType?: string;
  sourceId?: string;
  facilityType?: string; // e.g., "Tank Battery - (TB)", "Compressor Station - (CS)"
  facilityTypeCode?: string;
  wellNumber?: string;
  district?: string;
  leaseNumber?: string;
  oilGasCode?: string;
  fieldNumber?: string;
  pool?: string;
  spudDate?: string;
  completionDate?: string;
  plugDate?: string;
  totalDepth?: number;
  lateralLength?: number;
  wellType?: string;
  hasLeaseProduction?: boolean;
  hasWellProduction?: boolean;
  firstProductionMonth?: string;
  latestProductionMonth?: string;
  latestTxPermit?: TxPermitSummary;
  txActivitySummary?: TxActivitySummary;
  officialWater?: TxOfficialWaterSummary;
  txH2S?: TxH2SSummary;
  lifecycleSummary?: WellLifecycleSummary;
  nearbyOpsSummary?: NearbyOpsSummary;
  recentTxViolations?: TxViolationSummary[];
  operatorStatus?: OperatorStatusSummary;
  c148ProducedWaterVolumes?: C148ProducedWaterVolumesSummary;
  facilityDetails?: FacilityDetailSummary;
  distance?: number;
}

export interface UserSubscription {
  userId: string;
  tier: 'free' | 'basic' | 'premium';
  expiresAt?: Date;
  billingCustomerId?: string;
  billingSubscriptionId?: string;
}

export interface UserFavorite {
  id: number;
  userId: string;
  wellId: number;
  createdAt: Date;
}

export interface BasinInfo {
  id: string;
  name: string;
  description: string;
  region: string;
  bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  center: {
    lat: number;
    lng: number;
  };
}

// County-based filtering for well searches
export interface CountyInfo {
  name: string;
  wells: number;
  basin: string;
}

export const NEW_MEXICO_COUNTIES: CountyInfo[] = [
  { name: 'Bernalillo', wells: 0, basin: 'Central' },
  { name: 'Catron', wells: 0, basin: 'Southwest' },
  { name: 'Chaves', wells: 0, basin: 'Southeast' },
  { name: 'Cibola', wells: 0, basin: 'Northwest' },
  { name: 'Colfax', wells: 0, basin: 'Northeast' },
  { name: 'Curry', wells: 0, basin: 'East' },
  { name: 'De Baca', wells: 0, basin: 'East' },
  { name: 'Dona Ana', wells: 0, basin: 'South' },
  { name: 'Eddy', wells: 0, basin: 'Delaware' },
  { name: 'Grant', wells: 0, basin: 'Southwest' },
  { name: 'Guadalupe', wells: 0, basin: 'East' },
  { name: 'Harding', wells: 0, basin: 'Northeast' },
  { name: 'Hidalgo', wells: 0, basin: 'Southwest' },
  { name: 'Lea', wells: 0, basin: 'Delaware' },
  { name: 'Lincoln', wells: 0, basin: 'Southeast' },
  { name: 'Los Alamos', wells: 0, basin: 'Northwest' },
  { name: 'Luna', wells: 0, basin: 'Southwest' },
  { name: 'McKinley', wells: 0, basin: 'Northwest' },
  { name: 'Mora', wells: 0, basin: 'Northeast' },
  { name: 'Otero', wells: 0, basin: 'South' },
  { name: 'Quay', wells: 0, basin: 'East' },
  { name: 'Rio Arriba', wells: 0, basin: 'Northwest' },
  { name: 'Roosevelt', wells: 0, basin: 'Southeast' },
  { name: 'Sandoval', wells: 0, basin: 'Northwest' },
  { name: 'San Juan', wells: 0, basin: 'San Juan' },
  { name: 'San Miguel', wells: 0, basin: 'Northeast' },
  { name: 'Santa Fe', wells: 0, basin: 'North Central' },
  { name: 'Sierra', wells: 0, basin: 'Southwest' },
  { name: 'Socorro', wells: 0, basin: 'Southwest' },
  { name: 'Taos', wells: 0, basin: 'North' },
  { name: 'Torrance', wells: 0, basin: 'Central' },
  { name: 'Union', wells: 0, basin: 'Northeast' },
  { name: 'Valencia', wells: 0, basin: 'Central' }
];

// Basins kept for offline map downloads only
export type Basin = 'permian' | 'delaware' | 'san-juan' | 'raton';

export const BASINS: Record<Basin, BasinInfo> = {
  'permian': {
    id: 'permian',
    name: 'Permian Basin',
    description: 'Southeast New Mexico',
    region: 'Southeast',
    bounds: { north: 33.5, south: 31.5, east: -103.0, west: -104.5 },
    center: { lat: 32.5, lng: -103.75 }
  },
  'delaware': {
    id: 'delaware',
    name: 'Delaware Basin',
    description: 'Eddy & Lea Counties',
    region: 'Southeast',
    bounds: { north: 33.0, south: 32.0, east: -103.5, west: -104.5 },
    center: { lat: 32.5, lng: -104.0 }
  },
  'san-juan': {
    id: 'san-juan',
    name: 'San Juan Basin',
    description: 'Northwest New Mexico',
    region: 'Northwest',
    bounds: { north: 37.0, south: 35.5, east: -107.0, west: -109.0 },
    center: { lat: 36.25, lng: -108.0 }
  },
  'raton': {
    id: 'raton',
    name: 'Raton Basin',
    description: 'Northeast New Mexico',
    region: 'Northeast',
    bounds: { north: 37.0, south: 36.0, east: -104.0, west: -105.0 },
    center: { lat: 36.5, lng: -104.5 }
  }
};
